import React, { useState } from 'react'

import {
  generatePath,
  useHistory,
  useLocation,
  useParams,
} from 'react-router-dom'
import { HelpCircle as HelpCircleIcon } from 'react-feather'
import { Button } from '@material-ui/core'

import {
  Container,
  ContentHeader,
  GuestDriveTour,
  LoadingFeedback,
  Page,
} from 'components'
import AiInventoryForm from 'views/AiInventory/components/AiInventoryForm'

import useAuthGuest from 'hooks/useAuthGuest'
import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'

import constants from 'constants/index'
import * as service from 'service'
import helpers from 'helpers'
import { routes } from 'GuestRoutes'
import {
  buildGeneralInfoPayload,
  buildPurposesPayload,
  fromPersonalDataValue,
} from 'views/AiInventory/components/AiInventoryForm/formMappers'
import { AI_INVENTORY_FORM_DRIVER_STEPS } from './driverSteps'

const GUEST_STEPS = [
  'Informações Gerais',
  'Finalidade',
  'Detalhamento',
  'Resumo',
]

const AiInventoryFormPage = () => {
  const history = useHistory()
  const location = useLocation()
  const snackbar = useSnackbar()
  const { aiInventoryId, organizationSlug } = useParams()
  const { user } = useAuthGuest()
  const [openTour, setOpenTour] = useState(false)
  const isEdit = !!aiInventoryId
  const initialStep = location.state?.initialStep ?? 0

  const { response, isLoading, refresh } = useFetch(
    service.guestPortal.aiInventory.show,
    aiInventoryId,
    [aiInventoryId],
    isEdit,
  )

  const aiInventory = isEdit ? response?.data?.aiInventoryItem || null : null

  const handleStepSubmit = async (step, data) => {
    try {
      if (step === 0) {
        const payload = buildGeneralInfoPayload(data, {
          fallbackDepartmentId: user?.departmentId,
        })

        if (isEdit) {
          await service.guestPortal.aiInventory.update(aiInventoryId, payload)
          return
        }

        const createResponse = await service.guestPortal.aiInventory.create({
          ...payload,
          responsibleName: user?.responsibleName || null,
        })
        const newId = createResponse?.data?.aiInventory?.id
        history.push(
          generatePath(routes.aiInventory.edit, {
            organizationSlug,
            aiInventoryId: newId,
          }),
          { initialStep: 1 },
        )
      } else if (step === 1) {
        await service.guestPortal.aiInventory.update(
          aiInventoryId,
          buildPurposesPayload(data),
        )
      }
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao salvar. Tente novamente.',
        variant: 'error',
      })
      throw error
    }
  }

  const handlePersonalDataChange = async (value) => {
    try {
      await service.guestPortal.aiInventory.updateRiskAnalysis(aiInventoryId, {
        processesPersonalData: fromPersonalDataValue(value),
        riskOptionIds: aiInventory?.riskOptionIds || [],
      })
      refresh()
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao salvar dados pessoais.',
        variant: 'error',
      })
    }
  }

  const ready = !isEdit || !!aiInventory

  return (
    <Page title={isEdit ? 'Editar Inventário de IA' : 'Novo Inventário de IA'}>
      <Container maxWidth={false}>
        <LoadingFeedback open={isEdit && isLoading} />
        <ContentHeader title={isEdit ? 'Editar IA' : 'Nova IA'}>
          <Button
            id={constants.aiInventory.AI_INVENTORY_FORM_DRIVER_STEP_1}
            variant="contained"
            color="primary"
            startIcon={<HelpCircleIcon size={20} />}
            onClick={() => setOpenTour(true)}
          >
            Tutorial
          </Button>
        </ContentHeader>
        {ready && (
          <AiInventoryForm
            mode={isEdit ? 'edit' : 'new'}
            aiInventory={aiInventory}
            steps={GUEST_STEPS}
            guestMode
            currentDepartmentId={user?.departmentId || null}
            toolsService={service.guestPortal.aiInventoryTools.get}
            departmentsService={service.guestPortal.departments.get}
            purposesService={service.guestPortal.aiInventoryPurposes}
            onStepSubmit={handleStepSubmit}
            onPersonalDataChange={handlePersonalDataChange}
            onAiInventoryRefresh={refresh}
            onBack={() =>
              history.push(
                generatePath(routes.aiInventory.list, { organizationSlug }),
              )
            }
            defaultStep={initialStep}
          />
        )}
      </Container>
      <GuestDriveTour
        stepsMatrix={AI_INVENTORY_FORM_DRIVER_STEPS}
        open={openTour}
        setOpen={setOpenTour}
      />
    </Page>
  )
}

export default AiInventoryFormPage
